import React, { useState } from 'react';
import { useSimpleEmail } from './hooks/useSimpleEmail';

const MultiEmailSender = () => {
    const { sendEmail, sendBulkEmail, loading, error, success, resetState } = useSimpleEmail();

    const [emailData, setEmailData] = useState({
        recipients: '',
        subject: '',
        message: '',
        useCustomHtml: false,
        customHtml: ''
    });

    const [recipientCount, setRecipientCount] = useState(0);
    const [sendType, setSendType] = useState('single'); // 'single' o 'bulk'

    // Función para procesar los destinatarios
    const processRecipients = (recipientsString) => {
        const emails = recipientsString
            .split(/[,;\n]/) // Separar por comas, punto y coma o saltos de línea
            .map(email => email.trim())
            .filter(email => email && email.includes('@'));

        setRecipientCount(emails.length);
        setSendType(emails.length > 1 ? 'bulk' : 'single');

        return emails;
    };

    const handleRecipientsChange = (e) => {
        const value = e.target.value;
        setEmailData({ ...emailData, recipients: value });
        processRecipients(value);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        resetState();

        const recipients = processRecipients(emailData.recipients);

        if (recipients.length === 0) {
            alert('Por favor ingresa al menos un email válido');
            return;
        }

        try {
            const htmlContent = emailData.useCustomHtml && emailData.customHtml
                ? emailData.customHtml
                : null;

            if (sendType === 'single') {
                // Envío individual
                await sendEmail(
                    recipients[0],
                    emailData.subject,
                    emailData.message,
                    htmlContent
                );
            } else {
                // Envío masivo
                const result = await sendBulkEmail(
                    recipients,
                    emailData.subject,
                    emailData.message,
                    htmlContent
                );

                console.log('Resultado envío masivo:', result);

                // Mostrar estadísticas
                if (result.sent < result.total) {
                    alert(`Se enviaron ${result.sent} de ${result.total} emails correctamente`);
                }
            }

            // Limpiar formulario después del éxito
            if (success) {
                setEmailData({
                    recipients: '',
                    subject: '',
                    message: '',
                    useCustomHtml: false,
                    customHtml: ''
                });
                setRecipientCount(0);
            }

        } catch (err) {
            console.error('Error enviando emails:', err);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEmailData({
            ...emailData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    const addSampleEmails = () => {
        const samples = [
            'usuario1@ejemplo.com',
            'usuario2@ejemplo.com',
            'usuario3@ejemplo.com'
        ].join(', ');

        setEmailData({ ...emailData, recipients: samples });
        processRecipients(samples);
    };

    return (
        <div className="max-w-4xl mx-auto mt-8 p-6 bg-white rounded-lg shadow-lg">
            <div className="mb-6">
                <h2 className="text-3xl font-bold text-gray-800 mb-2">
                    📬 Envío de Emails Múltiple
                </h2>
                <p className="text-gray-600">
                    Envía emails a uno o varios destinatarios de forma fácil y rápida
                </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Destinatarios */}
                <div>
                    <div className="flex justify-between items-center mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                            Destinatarios:
                        </label>
                        <div className="flex space-x-2">
                            <button
                                type="button"
                                onClick={addSampleEmails}
                                className="text-xs bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded text-gray-600"
                            >
                                Agregar ejemplos
                            </button>
                            {recipientCount > 0 && (
                                <span className={`text-xs px-2 py-1 rounded ${sendType === 'bulk'
                                    ? 'bg-purple-100 text-purple-800'
                                    : 'bg-blue-100 text-blue-800'
                                    }`}>
                                    {recipientCount} destinatario{recipientCount > 1 ? 's' : ''}
                                    {sendType === 'bulk' ? ' (Envío masivo)' : ' (Envío individual)'}
                                </span>
                            )}
                        </div>
                    </div>
                    <textarea
                        name="recipients"
                        value={emailData.recipients}
                        onChange={handleRecipientsChange}
                        required
                        rows="4"
                        placeholder="Ingresa los emails separados por comas, punto y coma o saltos de línea:&#10;usuario1@ejemplo.com, usuario2@ejemplo.com&#10;usuario3@ejemplo.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                        Puedes separar los emails con comas (,), punto y coma (;) o saltos de línea
                    </p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                    {/* Asunto */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Asunto:
                        </label>
                        <input
                            type="text"
                            name="subject"
                            value={emailData.subject}
                            onChange={handleChange}
                            required
                            placeholder="Asunto del email"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>

                    {/* Tipo de envío (informativo) */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Tipo de envío:
                        </label>
                        <div className={`px-3 py-2 rounded-md text-sm font-medium ${sendType === 'bulk'
                            ? 'bg-purple-50 text-purple-700 border border-purple-200'
                            : recipientCount === 1
                                ? 'bg-blue-50 text-blue-700 border border-blue-200'
                                : 'bg-gray-50 text-gray-500 border border-gray-200'
                            }`}>
                            {recipientCount === 0 && '⏳ Esperando destinatarios...'}
                            {recipientCount === 1 && '📧 Envío individual'}
                            {recipientCount > 1 && `📮 Envío masivo (${recipientCount} emails)`}
                        </div>
                    </div>
                </div>

                {/* Mensaje */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Mensaje:
                    </label>
                    <textarea
                        name="message"
                        value={emailData.message}
                        onChange={handleChange}
                        required
                        rows="6"
                        placeholder="Escribe tu mensaje aquí...&#10;&#10;Puedes usar saltos de línea que se convertirán automáticamente en HTML."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    />
                </div>

                {/* HTML Personalizado */}
                <div>
                    <label className="flex items-center space-x-2 mb-2">
                        <input
                            type="checkbox"
                            name="useCustomHtml"
                            checked={emailData.useCustomHtml}
                            onChange={handleChange}
                            className="rounded text-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-700">
                            Usar HTML personalizado
                        </span>
                    </label>

                    {emailData.useCustomHtml && (
                        <div>
                            <textarea
                                name="customHtml"
                                value={emailData.customHtml}
                                onChange={handleChange}
                                rows="8"
                                placeholder="<div>&#10;  <h1>Mi Email Personalizado</h1>&#10;  <p>Contenido HTML aquí...</p>&#10;</div>"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm resize-none"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Si está vacío, se usará el template automático con el mensaje de arriba
                            </p>
                        </div>
                    )}
                </div>

                {/* Mensajes de Estado */}
                {error && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <span className="text-red-400">❌</span>
                            </div>
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-red-800">Error</h3>
                                <p className="mt-1 text-sm text-red-700">{error}</p>
                            </div>
                        </div>
                    </div>
                )}

                {success && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-md">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <span className="text-green-400">✅</span>
                            </div>
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-green-800">¡Éxito!</h3>
                                <p className="mt-1 text-sm text-green-700">
                                    {sendType === 'bulk'
                                        ? `Emails enviados correctamente a ${recipientCount} destinatarios`
                                        : 'Email enviado correctamente'
                                    }
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Botón Enviar */}
                <div className="flex justify-center">
                    <button
                        type="submit"
                        disabled={loading || recipientCount === 0}
                        className={`px-8 py-3 rounded-lg font-semibold transition-all duration-200 ${loading || recipientCount === 0
                            ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                            : sendType === 'bulk'
                                ? 'bg-purple-500 hover:bg-purple-600 text-white shadow-md hover:shadow-lg'
                                : 'bg-blue-500 hover:bg-blue-600 text-white shadow-md hover:shadow-lg'
                            }`}
                    >
                        {loading ? (
                            <div className="flex items-center">
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                {sendType === 'bulk' ? `Enviando ${recipientCount} emails...` : 'Enviando email...'}
                            </div>
                        ) : (
                            <>
                                {sendType === 'bulk'
                                    ? `📮 Enviar a ${recipientCount} destinatarios`
                                    : recipientCount === 1
                                        ? '📧 Enviar Email'
                                        : '✉️ Enviar Email'
                                }
                            </>
                        )}
                    </button>
                </div>
            </form>

            {/* Información adicional */}
            <div className="mt-8 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-2">💡 Consejos:</h3>
                <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Puedes pegar múltiples emails desde Excel o un archivo de texto</li>
                    <li>• Los emails inválidos se filtran automáticamente</li>
                    <li>• El envío masivo procesa cada email individualmente</li>
                    <li>• Si no usas HTML personalizado, se aplicará un template automático</li>
                    <li>• Los emails se envían desde: <strong>soporte@miguel.app</strong></li>
                </ul>
            </div>
        </div>
    );
};

export default MultiEmailSender;